import { useState } from 'react';
import { Button, Title1 } from '@fluentui/react-components';
import { Sparkle24Regular, Settings24Regular } from '@fluentui/react-icons';
import { useCurrentEmail } from './hooks/useCurrentEmail';
import { useDraftGeneration } from './hooks/useDraftGeneration';
import { useDraftStore } from './stores/draftStore';
import { useSettingsStore } from './stores/settingsStore';
import { EmailSummary } from './components/email/EmailSummary';
import { DraftPanel } from './components/draft/DraftPanel';
import { ToneSelector } from './components/draft/ToneSelector';
import { Spinner } from './components/common/Spinner';

function App() {
  const { email, isLoading: emailLoading } = useCurrentEmail();
  const { generateDraft, isGenerating, error } = useDraftGeneration();
  const { draft, selectedTone, setSelectedTone, attachmentStatus, attachmentCount, attachmentWarning } = useDraftStore();
  const { defaultTone } = useSettingsStore();
  const [showSettings, setShowSettings] = useState(false);

  const currentTone = selectedTone || defaultTone;

  const handleGenerate = () => {
    generateDraft({ tone: currentTone });
  };

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-gray-50)',
      }}
    >
      {/* Header */}
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 'var(--spacing-md)',
          backgroundColor: 'white',
          borderBottom: '1px solid var(--color-gray-200)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Sparkle24Regular style={{ color: 'white', fontSize: '20px' }} />
          </div>
          <Title1 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--color-gray-900)' }}>
            AutoResponder
          </Title1>
        </div>

        <Button
          appearance="subtle"
          icon={<Settings24Regular />}
          onClick={() => setShowSettings(!showSettings)}
          aria-label="Settings"
        />
      </header>

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          overflow: 'auto',
          padding: 'var(--spacing-md)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-md)',
        }}
      >
        {/* Current Email Info */}
        <EmailSummary
          subject={email?.subject}
          sender={email?.sender ?? null}
          bodyPreview={email?.bodyPreview ?? null}
          isLoading={emailLoading}
        />

        {/* No draft yet - show generate button */}
        {!draft && !isGenerating && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--spacing-md)',
              padding: 'var(--spacing-lg)',
              backgroundColor: 'white',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-gray-200)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 'var(--spacing-sm)',
              }}
            >
              <ToneSelector
                value={currentTone}
                onChange={setSelectedTone}
                disabled={!email?.id}
              />
            </div>

            <Button
              appearance="primary"
              icon={<Sparkle24Regular />}
              onClick={handleGenerate}
              disabled={!email?.id || isGenerating}
              style={{ alignSelf: 'stretch' }}
              size="large"
            >
              Generate Draft Reply
            </Button>

            {!email?.id && (
              <div
                style={{
                  fontSize: '12px',
                  color: 'var(--color-gray-500)',
                  textAlign: 'center',
                }}
              >
                Select an email to generate a draft reply
              </div>
            )}
          </div>
        )}

        {/* Generating state */}
        {isGenerating && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--spacing-md)',
              padding: 'var(--spacing-xl)',
              backgroundColor: 'white',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--color-gray-200)',
            }}
          >
            <Spinner size="large" />
            <div style={{ color: 'var(--color-gray-600)', textAlign: 'center' }}>
              <div style={{ fontWeight: 500 }}>
                {attachmentStatus === 'loading' ? 'Loading attachments...' : 'Generating draft...'}
              </div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                {attachmentStatus === 'loading'
                  ? 'Retrieving email attachments for analysis'
                  : `Analyzing email${attachmentCount > 0 ? ` and ${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}` : ''} for a ${currentTone} response`}
              </div>
            </div>
          </div>
        )}

        {/* Attachment warning (e.g., personal account limitation) */}
        {attachmentWarning && !isGenerating && (
          <div
            style={{
              padding: 'var(--spacing-md)',
              backgroundColor: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: 'var(--radius-md)',
              color: '#92400e',
              fontSize: '13px',
            }}
          >
            {attachmentWarning}
          </div>
        )}

        {/* Error state */}
        {error && (
          <div
            style={{
              padding: 'var(--spacing-md)',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 'var(--radius-md)',
              color: '#991b1b',
            }}
          >
            <div style={{ fontWeight: 500 }}>Error generating draft</div>
            <div style={{ fontSize: '13px', marginTop: '4px' }}>{error}</div>
          </div>
        )}

        {/* Draft Panel */}
        {draft && !isGenerating && <DraftPanel />}
      </main>

      {/* Footer */}
      <footer
        style={{
          padding: 'var(--spacing-sm) var(--spacing-md)',
          backgroundColor: 'white',
          borderTop: '1px solid var(--color-gray-200)',
          fontSize: '11px',
          color: 'var(--color-gray-400)',
          textAlign: 'center',
        }}
      >
        AI-generated drafts require human review before sending
      </footer>
    </div>
  );
}

export default App;
